import { useState, useEffect, useMemo, useRef } from "react";
import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps";
import Search from "../../src/assets/svg/search.svg";
import MarkerIcon from "../../src/assets/svg/mapsMarker.svg";
import XIcon from "../../src/assets/svg/x.svg";
import { Dealer } from "../data/models";
import { useMap } from "@vis.gl/react-google-maps";

export default function SelectDealerMap({
  initialId = 382700,
  onChange,
  dealers,
}: {
  initialId?: number;
  onChange?: (selectedId: number, dealer: Dealer) => void;
  dealers: Dealer[];
}) {
  const initial = dealers.find((d) => d.id === initialId) ?? dealers[0];

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Dealer>(initial);

  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!dealers?.length) return;

    const next = dealers.find((d) => d.id === initialId) ?? dealers[0];
    if (!selected || selected.id !== next.id) {
      setSelected(next);
      onChange?.(next.id, next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialId, dealers]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [dealers]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return dealers;
    return dealers.filter((d) =>
      [d.label, ...d.badges].some((t) => t.toLowerCase().includes(q)),
    );
  }, [query, dealers]);

  const apiKey =
    (import.meta as any).env?.VITE_GOOGLE_MAPS_API_KEY ||
    (import.meta as any).env?.GOOGLE_MAPS_API_KEY ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // center direktno iz dealera (nema geokodiranja)
  const center =
    selected?.lat && selected?.lng
      ? { lat: selected.lat, lng: selected.lng }
      : { lat: 45.815, lng: 15.9819 }; // fallback Zagreb centar

  return (
    <APIProvider apiKey={apiKey}>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium ">
            {dealers.length > 1 ? "Odaberite auto centar" : "Auto centar"}
            <span className="pl-1 text-[13px] text-warning-red">*</span>
          </span>
          {dealers.length > 1 && (
            <span className="text-xs font-medium text-[#7B7B7B]">
              Odabrano: {selected?.label}
            </span>
          )}
        </div>

        {dealers.length > 1 ? (
          <div>
            <div className="flex items-center p-3 gap-2 bg-white border-x border-t border-[#E8E8E8]">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Unesite ime auto centra ili grada"
                className="flex-1 text-xs font-medium outline-none placeholder:text-[#7B7B7B]"
                autoFocus
              />
              <img src={Search} alt="Search" className="cursor-pointer" />
              <img
                src={XIcon}
                alt="Close"
                className="cursor-pointer"
                onClick={() => setQuery("")}
              />
            </div>

            <div className="relative flex flex-col w-full h-full gap-2 overflow-hidden">
              <div className="flex-1">
                <div
                  ref={listRef}
                  role="listbox"
                  className="flex flex-col gap-3 h-[212px] overflow-x-hidden overflow-y-auto bg-white border-x border-b p-3 pt-1 border-[#E8E8E8]"
                >
                  <ul className="flex flex-col gap-3">
                    {filtered.map((d, idx) => (
                      <li key={d.id} className="-mx-3">
                        <button
                          type="button"
                          onClick={() => {
                            setSelected(d);
                            onChange?.(d.id, d);
                          }}
                          className={
                            "relative w-full px-3 text-left flex flex-col gap-2.5 outline-none " +
                            (d.id === selected.id
                              ? "bg-[#f1f1f1] py-2 -my-2 before:content-[''] before:absolute before:left-0 before:inset-y-0 before:w-[3px] before:bg-primary-gray"
                              : "hover:bg-neutral-50 py-2 -my-2")
                          }
                        >
                          <div className="text-xs font-semibold">{d.label}</div>
                          <div className="flex flex-wrap gap-1">
                            {d.badges.map((b, i) => (
                              <span
                                key={i}
                                className={
                                  "text-[10px] px-1.5 py-1 font-medium " +
                                  (d.id === selected.id
                                    ? "bg-primary-gray text-white"
                                    : "bg-[#F2F2F2]")
                                }
                              >
                                {b}
                              </span>
                            ))}
                          </div>
                        </button>
                        {idx !== filtered.length - 1 && (
                          <hr className="-mx-3 border-t border-[#F6F6F6] mt-3" />
                        )}
                      </li>
                    ))}

                    {!filtered.length && (
                      <li className="py-4 text-sm text-[#7B7B7B] flex justify-center">
                        Nema rezultata.
                      </li>
                    )}
                  </ul>
                </div>

                <input type="hidden" name="dealer_id" value={selected.id} />
              </div>
            </div>
          </div>
        ) : (
          <input
            id={"dealer"}
            value={dealers[0].label}
            readOnly={true}
            className="inline-flex w-full justify-between border p-3 focus:!border-extra-light appearance-none focus:outline-none text-[15px] bg-gray-light text-opacity-50"
          />
        )}

        <Map
          className="inset-0 w-full h-[384px]"
          defaultCenter={center}
          defaultZoom={15}
          gestureHandling="greedy"
          disableDefaultUI
        >
          <PanToSelected center={center} />
          {selected?.lat && selected?.lng && (
            <Marker
              position={{ lat: selected.lat, lng: selected.lng }}
              icon={MarkerIcon}
            />
          )}
        </Map>
      </div>
    </APIProvider>
  );
}

function PanToSelected({ center }: { center: { lat: number; lng: number } }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    map.panTo(center);
  }, [map, center.lat, center.lng]);

  return null;
}
